import type { ReactElement } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DecisionPriority, NoApproversStage } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useRecordProcessPermissions } from '@/hooks/useActiveInstanceForRecord';
import type { DecisionGate } from '@/hooks/useSaveBeforeDecision';
import {
  type BulkMissingField,
  noApproversMessage,
  startErrorI18nKey,
  startRequiredFieldsMissingMessage,
} from '@/lib/processes/approvals-api';
import {
  fetchProcessesForModule,
  type ProcessClassifierFilter,
  startProcess,
} from '@/lib/processes/instance-api';

import { ProcessPickerModal, type ProcessPickerModalHandle } from './ProcessPickerModal';

/**
 * Odmowy startu znaczące „okno pokazywało nieaktualne ustawienia etapu albo pulę akceptantów":
 * etap zaczął wymagać adresata (`ADDRESSEE_REQUIRED`), wskazana osoba wypadła z puli
 * (`ADDRESSEE_NOT_IN_POOL`) albo etap przestał pozwalać na wskazanie jednej osoby
 * (`ADDRESSEE_NOT_ALLOWED`). Po każdej z nich okno zostaje otwarte i pobiera pulę od nowa.
 */
const STALE_POOL_START_ERRORS: ReadonlySet<string> = new Set([
  'ADDRESSEE_REQUIRED',
  'ADDRESSEE_NOT_IN_POOL',
  'ADDRESSEE_NOT_ALLOWED',
]);

/**
 * Prawo startu obiegu. Z `me.permissions` (firma aktywna) czytane TYLKO dla rekordu bez
 * identyfikatora — patrz JSDoc komponentu; dla pozostałych rekordów prawo daje serwer.
 */
const START_PERMISSION = 'processes.instances.create';

export interface SubmitForApprovalButtonProps {
  /** Kod modułu docelowego, np. `crm_core.contractors`. */
  moduleCode: string;
  /** ID rekordu, którego dotyczy proces. Może być puste, gdy podano `resolveRecordId`. */
  recordId: string;
  /**
   * Leniwe ustalenie identyfikatora rekordu — wołane po kliknięciu, PRZED otwarciem wyboru
   * procesu. Dla celów, które nie mają jeszcze wiersza w naszej bazie: dokument z Symfonii
   * Handel dostaje identyfikator dopiero w chwili wysyłania do akceptacji (rezerwacja).
   * Robienie tego przy wejściu na formatkę oznaczałoby zapis przy każdym podglądzie dokumentu.
   */
  resolveRecordId?: () => Promise<string>;
  /** Callback po pomyślnym starcie (np. refetch bannera na karcie). */
  onSubmitted?: () => void;
  testIdPrefix?: string;
  /**
   * F17/klasyfikatory — typ bieżącego rekordu (oś typu: typ dokumentu dla `crm_core.documents`,
   * typ zadania dla `crm_core.tasks`). Dla modułów z osią typu ZAWSZE podany. Ma pierwszeństwo
   * nad `categoryId`.
   */
  typeId?: string | null;
  /**
   * Kategoria bieżącego rekordu (oś kategorii — pozostałe moduły). `null`/brak
   * = rekord bez kategorii → filtr `uncategorized=true` (koszyk „Bez kategorii").
   */
  categoryId?: string | null;
  /**
   * Bramka przed otwarciem okna wyboru procesu (formularz w edycji: najpierw zwykły zapis, okno
   * dopiero po jego udanym zapisie — patrz `useSaveBeforeDecision`). Klik woła `beforeSubmit(open)`
   * zamiast otwierać okno od razu; brak propsa = okno otwiera się od razu. Flagi wyglądu na
   * bramce: `saving` (zapis formularza trwa — przycisk zablokowany, bez kręciołka) i `waiting`
   * (okno czeka na wynik zapisu z tego kliknięcia — kręciołek).
   *
   * Po udanym zapisie typ/kategoria rekordu mogły się zmienić, więc lista procesów może być inna:
   * okno otwiera się dopiero, gdy lista dla BIEŻĄCEGO typu/kategorii jest wczytana — do tego czasu
   * przycisk zostaje na ekranie, zablokowany z kręciołkiem (nie znika na czas wczytywania). Gdy
   * lista jest pusta (np. zmieniono typ dokumentu na typ bez procesu), okno się nie otwiera,
   * przycisk znika, a dymek informacyjny mówi, że zapisano i procesu dla rekordu nie ma. Formularz
   * musi wołać bramkę dopiero po odświeżeniu rekordu (typ/kategoria), patrz `useSaveBeforeDecision`.
   */
  beforeSubmit?: DecisionGate;
}

/**
 * Przycisk „Wyślij do akceptacji" na karcie rekordu (F17 T17.1.7).
 * Pobiera opublikowane procesy dla modułu; gdy brak — przycisk ukryty.
 * Klik otwiera `ProcessPickerModal` (wybór procesu + komentarz inicjatora),
 * a potwierdzenie startuje instancję.
 *
 * Widoczność wg flagi `canStart` z serwera, czyli tej samej bramki co sam start: dostęp do firmy
 * REKORDU + prawo `processes.instances.create` w tej firmie + dostęp do rekordu (z jego kategorią
 * kontrahenta / typem dokumentu) — nie wg firmy aktywnej w aplikacji (decyzja właściciela
 * 2026-10-08: „firma z rekordu, nie ze sprawy"). Start jest więc możliwy także w trybie „Wszystkie
 * firmy", a faktura firmy B otwarta przy wybranej firmie A liczy się wg B. Flagę daje serwer w
 * odpowiedzi listingu spraw rekordu (`recordPermissions`, ten sam klucz zapytania co baner — bez
 * dodatkowego żądania); superużytkownik widzi przycisk zawsze. Rekord z identyfikatorem bez
 * odpowiedzi (ładowanie, błąd) = brak prawa, przycisk ukryty (fail-closed).
 * Jedyny wyjątek: rekord BEZ identyfikatora (`recordId` puste, dokument z Symfonii przed
 * rezerwacją — patrz `resolveRecordId`) nie ma jeszcze własnej firmy ani sprawy do zapytania;
 * powstanie w firmie aktywnej w aplikacji, więc liczy się prawo `processes.instances.create` z
 * `me.permissions` (firma aktywna).
 * Prawo i tak sprawdza serwer przy starcie — flaga służy tylko widoczności. Gdy prawo zniknie, a
 * okno (albo prośba o okno z bramki) jest otwarte, stan okna jest zerowany, a dymek informacyjny
 * mówi dlaczego — okno nie wraca samo, gdy prawo się pojawi ponownie. Dymek tylko wtedy, gdy prawo
 * odebrał wprost serwer (flaga `false`) albo zniknęło z `me.permissions`; gdy flag po prostu nie
 * znamy (nieudane odświeżenie), okno zamyka się bez dymka, bo nie wiemy, czy prawo faktycznie
 * zniknęło.
 *
 * Fokus klawiatury przy bramce (`beforeSubmit`): przycisk na czas zapisu staje się `disabled`, a
 * przeglądarka zdejmuje z niego fokus — okno wyboru procesu zapamiętałoby wtedy `body` i po
 * zamknięciu oddało fokus na początek strony. Dlatego, gdy przycisk miał fokus w chwili kliknięcia,
 * po zamknięciu okna dostaje go z powrotem — tylko gdy fokus jest nigdzie (`body`), a przycisk
 * nadal jest w DOM i nie jest zablokowany (jak w `ApprovalActionsPanel`). Pamiętamy samą flagę
 * „oddać fokus", nie węzeł DOM: przycisk bywa w międzyczasie odmontowany i zamontowany od nowa
 * (np. gdy lista procesów po zmianie typu wczytuje się, a okno jeszcze nie zostało poproszone o
 * otwarcie), więc w chwili oddania fokusu bierzemy BIEŻĄCY przycisk (`buttonRef.current`).
 */
export function SubmitForApprovalButton({
  moduleCode,
  recordId,
  resolveRecordId,
  onSubmitted,
  testIdPrefix = 'submit-approval',
  typeId,
  categoryId,
  beforeSubmit,
}: SubmitForApprovalButtonProps): ReactElement | null {
  const { t } = useTranslation();
  const { show } = useToast();
  const { me } = useTenantAuth();
  const queryClient = useQueryClient();
  // Prawo startu w firmie rekordu — z odpowiedzi listingu spraw rekordu (ten sam klucz zapytania co
  // baner, więc bez dodatkowego żądania); `null`, gdy rekord nie ma jeszcze identyfikatora albo
  // odpowiedź nie dotarła.
  const recordPermissions = useRecordProcessPermissions(moduleCode, recordId);
  // Widoczny tylko z uprawnieniem startu (T17.1.8b); o „slocie wolnym" decyduje baner. Prawo liczy
  // się w firmie REKORDU, nie w firmie aktywnej w aplikacji (decyzja właściciela 2026-10-08) —
  // flagę `canStart` daje serwer razem z listą spraw rekordu (patrz JSDoc komponentu). Rekord z
  // identyfikatorem, dla którego odpowiedzi jeszcze nie ma (ładowanie, błąd), = brak prawa.
  const hasStartRight = recordId
    ? recordPermissions?.canStart === true
    : (me?.permissions.includes(START_PERMISSION) ?? false);
  const canStart = !!me && (me.user.isSuperUser || hasStartRight);
  // Brak prawa „wprost": serwer odpowiedział `canStart: false` (albo rekord bez identyfikatora, gdzie
  // prawo czytamy z `me.permissions`) — w odróżnieniu od „nie wiemy" (ładowanie, błąd odświeżenia
  // flag: `recordPermissions === null`). Tylko wtedy wolno powiedzieć użytkownikowi „nie masz prawa".
  const startRightDenied = !canStart && (!recordId || recordPermissions !== null);
  const [open, setOpen] = useState(false);
  // Okno „do otwarcia": bramka (`beforeSubmit`) oddała funkcję otwarcia po udanym zapisie, ale okno
  // czeka jeszcze na listę procesów dla bieżącego typu/kategorii. Zawsze rozstrzygane do końca
  // (okno otwarte albo komunikat) i zerowane — nie zostaje „wiszące".
  const [openRequested, setOpenRequested] = useState(false);
  const [resolvedId, setResolvedId] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Flaga „oddać fokus przyciskowi po zamknięciu okna otwartego przez bramkę" (przycisk miał fokus w
  // chwili kliknięcia); `false` = nic nie oddajemy. Flaga, nie węzeł DOM — patrz JSDoc komponentu.
  const returnFocusRef = useRef(false);
  // Uchwyt okna wyboru procesu — po odmowie startu z powodu puli/ustawień etapu wołamy przez niego
  // `refreshPool` (szczegóły: komentarz przy `STALE_POOL_START_ERRORS` i w `onError`).
  const pickerRef = useRef<ProcessPickerModalHandle>(null);
  const effectiveRecordId = resolvedId ?? recordId;

  const openPicker = useCallback(async (): Promise<void> => {
    if (!resolveRecordId) {
      setOpen(true);
      return;
    }
    setResolving(true);
    try {
      setResolvedId(await resolveRecordId());
      setOpen(true);
    } catch {
      // Okno się nie otworzy, więc nie ma czego zamykać — flaga fokusu nie może przeżyć do kolejnego okna.
      returnFocusRef.current = false;
      show({
        variant: 'error',
        title: t('tenant.processes.submitButton.resolveError', 'Nie udało się przygotować sprawy'),
      });
    } finally {
      setResolving(false);
    }
  }, [resolveRecordId, show, t]);

  function handleClick(): void {
    if (!beforeSubmit) {
      void openPicker();
      return;
    }
    // Czy przycisk ma fokus w chwili kliknięcia (przy bramce wróci do niego fokus po zamknięciu okna).
    const hadFocus = buttonRef.current !== null && document.activeElement === buttonRef.current;
    beforeSubmit(() => {
      returnFocusRef.current = hadFocus;
      setOpenRequested(true);
    });
  }

  // F17/klasyfikatory — dokładnie JEDEN z trzech: typ rekordu > kategoria >
  // „Bez kategorii" (gdy oś kategorii, a rekord jej nie ma). Ten sam obiekt
  // idzie do klucza react-query I do wywołania fetch — zmiana typu/kategorii
  // rekordu (inny obiekt) odświeża listę procesów automatycznie.
  const classifier: ProcessClassifierFilter = typeId
    ? { typeId }
    : categoryId
      ? { categoryId }
      : { uncategorized: true };

  const processesQuery = useQuery({
    queryKey: ['processes', 'for-module', moduleCode, classifier],
    queryFn: () => fetchProcessesForModule(moduleCode, classifier),
  });

  const startMutation = useMutation({
    mutationFn: ({
      processId,
      comment,
      priority,
      deadlineAt,
      addressees,
    }: {
      processId: string;
      comment: string;
      priority: DecisionPriority;
      deadlineAt: string | undefined;
      addressees: Array<{ nodeId: string; userId: string }>;
    }) =>
      startProcess(processId, {
        targetRecordId: effectiveRecordId,
        initiatorComment: comment.trim() ? comment.trim() : undefined,
        priority,
        ...(deadlineAt ? { deadlineAt } : {}),
        ...(addressees.length > 0 ? { addressees } : {}),
      }),
    onSuccess: () => {
      show({
        variant: 'success',
        title: t('tenant.processes.picker.started', 'Wysłano do akceptacji'),
      });
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['processes'] });
      onSubmitted?.();
    },
    onError: (
      err: AxiosError<{
        error?: string;
        stages?: NoApproversStage[];
        missing?: BulkMissingField[];
      }>,
      variables,
    ) => {
      const errorCode = err.response?.data?.error;
      // Pierwszy etap (albo gałąź rozdzielenia) bez akceptantów — sprawa nie powstała; nazwy
      // etapów z odpowiedzi mówią, czego dotyczy prośba do administratora.
      if (errorCode === 'NO_APPROVERS') {
        show({ variant: 'error', title: noApproversMessage(t, err.response?.data?.stages) });
        return;
      }
      // Proces wymaga pól, których rekord nie ma (np. opiekun dokumentu) — sprawa nie powstała.
      // Okno ZOSTAJE otwarte (jak przy innych odmowach); lista etykiet mówi, co uzupełnić na karcie
      // rekordu, zanim wyślemy jeszcze raz.
      if (errorCode === 'REQUIRED_FIELDS_MISSING') {
        show({
          variant: 'error',
          title: startRequiredFieldsMissingMessage(t, err.response?.data?.missing),
        });
        return;
      }
      // Ustawienia etapu albo pula akceptantów zmieniły się po otwarciu okna: okno ZOSTAJE
      // otwarte (komentarz, priorytet, termin i wybrany proces bez zmian), a pula pobiera się
      // od nowa z pominięciem 30-sekundowej kopii; nieaktualne wybory adresatów okno zeruje
      // samo. Dalej — zwykła ścieżka: toast z własnym tekstem kodu.
      if (errorCode !== undefined && STALE_POOL_START_ERRORS.has(errorCode)) {
        pickerRef.current?.refreshPool(variables.processId);
      }
      // Inna odmowa startu z własnym komunikatem (np. błędnie narysowany start procesu) —
      // sprawa nie powstała, użytkownik dostaje powód i adresata prośby.
      const startErrorKey = startErrorI18nKey(errorCode);
      if (startErrorKey !== undefined) {
        show({ variant: 'error', title: t(startErrorKey) });
        return;
      }
      show({
        variant: 'error',
        title: t('tenant.processes.picker.startError', 'Nie udało się wysłać do akceptacji'),
      });
    },
  });

  const processes = processesQuery.data ?? [];
  // Lista jest „wczytana dla bieżącego klasyfikatora", gdy zapytanie o jego klucz nie czeka na
  // pierwsze dane ani nie odświeża się (po zmianie typu/kategorii klucz jest nowy, więc do czasu
  // odpowiedzi lista z poprzedniego klucza się nie liczy).
  const listSettled = !processesQuery.isPending && !processesQuery.isFetching;

  // Rozstrzygnięcie okna „do otwarcia" (bramka oddała funkcję otwarcia po udanym zapisie): dopiero
  // gdy lista dla bieżącego typu/kategorii jest wczytana — otwieramy okno albo, gdy listy nie ma
  // (pusta albo błąd wczytania BEZ danych), mówimy o tym dymkiem. Błąd odświeżenia przy danych
  // już w pamięci (np. ta sama lista wczytana wcześniej) nie jest brakiem listy: okno otwiera się
  // z nimi. Prośba jest zerowana w każdej gałęzi.
  const listLoadFailed = processesQuery.isError && processesQuery.data === undefined;
  useEffect(() => {
    // Bez prawa startu okna nie otwieramy (prośbę zeruje efekt niżej).
    if (!openRequested || !listSettled || !canStart) return;
    setOpenRequested(false);
    if (listLoadFailed) {
      returnFocusRef.current = false;
      show({
        variant: 'error',
        title: t('tenant.processes.submitButton.listLoadErrorAfterSave'),
      });
      return;
    }
    if (processes.length === 0) {
      returnFocusRef.current = false;
      show({
        variant: 'info',
        title: t('tenant.processes.submitButton.noProcessAfterSave'),
      });
      return;
    }
    void openPicker();
  }, [openRequested, listSettled, canStart, listLoadFailed, processes.length, openPicker, show, t]);

  // Prawo startu zniknęło (np. rekord przeniesiono do firmy, w której użytkownik nie ma tego
  // prawa, a flagi odświeżyły się): przycisk znika razem z oknem, więc stan „otwarte" ani „do
  // otwarcia" nie może zostać — inaczej okno wyskoczyłoby samo, gdy prawo wróci. Zależność od
  // `open` / `openRequested` łapie też prośbę o okno, która dotarła już PO utracie prawa (bramka
  // kończy zapis później). Zerujemy też flagę oddania fokusu (okna już nie będzie). Okno albo prośba
  // o nie znikają na oczach użytkownika (klik w „Wyślij…", zapis, nic się nie otwiera), więc gdy
  // serwer wprost odebrał prawo, mówi o tym dymek informacyjny. Efekt biegnie raz na utratę: po
  // wyzerowaniu stanu kolejny przebieg nie ma już okna ani prośby, więc dymka nie powtarza.
  useEffect(() => {
    if (canStart) return;
    if ((open || openRequested) && startRightDenied) {
      show({
        variant: 'info',
        title: t('tenant.processes.submitButton.startRightLost'),
      });
    }
    setOpen(false);
    setOpenRequested(false);
    returnFocusRef.current = false;
  }, [canStart, startRightDenied, open, openRequested, show, t]);

  // Okno otwarte, a lista procesów zrobiła się pusta (typ/kategoria rekordu zmieniły się tuż po
  // otwarciu): przycisk znika razem z oknem, więc stan „otwarte" nie może zostać — inaczej okno
  // wyskoczyłoby samo po kolejnej zmianie typu na taki, który ma proces.
  useEffect(() => {
    if (open && listSettled && processes.length === 0) setOpen(false);
  }, [open, listSettled, processes.length]);

  // Po zamknięciu okna otwartego przez bramkę: fokus wraca na przycisk, jeśli nigdzie nie trafił
  // (przycisk zdjął go, gdy na czas zapisu zrobił się `disabled`, więc okno zapamiętało `body`).
  // Bierzemy BIEŻĄCY przycisk (`buttonRef.current`), nie węzeł z chwili kliknięcia — mógł być w
  // międzyczasie odmontowany i zamontowany od nowa. Efekt biegnie po sprzątnięciu okna (jego dzieci
  // sprzątają wcześniej), więc `activeElement` jest już ostateczny. Fokus ustawiony gdzie indziej,
  // przycisk usunięty z DOM albo zablokowany → nic.
  useEffect(() => {
    if (open || !returnFocusRef.current) return;
    returnFocusRef.current = false;
    const target = buttonRef.current;
    const active = document.activeElement;
    const focusLost = active === null || active === document.body;
    if (focusLost && target !== null && target.isConnected && !target.matches(':disabled')) {
      target.focus();
    }
  }, [open]);

  if (!canStart) return null;

  // Lista procesów dla bieżącego typu/kategorii: wczytana i niepusta. Przy aktywnej prośbie o okno
  // (`openRequested`) przycisk NIE znika na czas wczytywania nowej listy — zostaje zablokowany z
  // kręciołkiem aż do otwarcia okna (albo dymku, gdy listy nie ma); wtedy renderujemy samego
  // przycisk, bez okna. Bez prośby o okno: brak listy = brak przycisku.
  const hasProcesses = !processesQuery.isPending && processes.length > 0;
  if (!hasProcesses && !openRequested) return null;

  // Wygląd przy bramce: zapis trwa (`saving`, w tym zwykłe „Zapisz") albo okno czeka na wynik
  // zapisu / na listę procesów (`waiting`, `openRequested`) → przycisk zablokowany; kręciołek tylko
  // wtedy, gdy czekamy na okno z tego kliknięcia (zwykłe „Zapisz" blokuje bez kręciołka).
  const waiting = beforeSubmit?.waiting === true || openRequested;
  const blocked = waiting || beforeSubmit?.saving === true;

  // Układ (fragment: przycisk, potem okno lub nic) jest stały, więc przycisk zachowuje ten sam węzeł
  // DOM (i fokus) także wtedy, gdy okna jeszcze nie ma.
  return (
    <>
      <Button
        ref={buttonRef}
        size="sm"
        data-testid={`${testIdPrefix}-submit-btn`}
        onClick={handleClick}
        disabled={resolving || blocked}
        loading={waiting}
      >
        {t('tenant.processes.submitButton.label', 'Wyślij do akceptacji')}
      </Button>
      {hasProcesses ? (
        <ProcessPickerModal
          ref={pickerRef}
          open={open}
          processes={processes}
          targetRecordId={effectiveRecordId}
          submitting={startMutation.isPending}
          onConfirm={(processId, comment, priority, deadlineAt, addressees) =>
            startMutation.mutate({ processId, comment, priority, deadlineAt, addressees })
          }
          onClose={() => setOpen(false)}
          testIdPrefix={testIdPrefix}
        />
      ) : null}
    </>
  );
}
